import React from 'react';

import type { HubItem } from '../api/types';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { PlateWindow } from './Plate';
import { buildNewsPath } from '../utils/contentPaths';

const formatDate = (iso: string): string => {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d
        .toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
        .toUpperCase();
};

/**
 * One dispatch: the news feed's row, plate on the left and the record on the right.
 *
 * Where the library gives a piece a whole column, the feed keeps everything on one
 * line of reading — a dated record you scan down. The rule between dispatches is the
 * only structure; the plate carries its own frame.
 *
 * Two things in the Stitch original are deliberately absent. The date was set in the
 * accent, which would have put orange text on the page and broken the apricot rule —
 * it is `text-ink-muted` here. And the reference ID (`REF: POL-882`) is not adopted at
 * all: no such identifier exists on a news item, and minting one for the look of it
 * would be inventing a fact. The slot it occupied carries the category instead, which
 * is true.
 */
const HubItemDispatch: React.FC<{ item: HubItem }> = ({ item }) => (
  <article className="group border-b border-rule">
    <NavLink
      to={buildNewsPath(item.slug || item.id)}
      className="flex flex-col gap-8 py-10 transition-colors duration-fast ease-sheet md:flex-row md:gap-12"
    >
      <div className="w-full shrink-0 md:w-1/3">
        <PlateWindow
          pictureUrl={item.pictureUrl}
          reservedLabel={item.categoryTitle}
          mounted
          imageClassName="sepia-[.65] saturate-125 contrast-90 brightness-110 opacity-90 transition-[filter,opacity] duration-fast ease-sheet group-hover:sepia-0 group-hover:saturate-100 group-hover:contrast-100 group-hover:brightness-100 group-hover:opacity-100"
        />
      </div>

      <div className="flex w-full min-w-0 flex-col md:w-2/3">
        {/* Date, then a rule running out to the category — the survey sheet's own way of
            tying a label to its row. The rule is hidden on narrow screens, where there
            is no width for it to cross. */}
        <div className="flex items-center gap-4">
          {item.date && (
            <time className="shrink-0 font-ui text-step--2 font-medium uppercase tracking-[0.18em] text-ink-muted">
              {formatDate(item.date)}
            </time>
          )}
          <span aria-hidden="true" className="hidden h-px flex-1 bg-rule md:block" />
          {item.categoryTitle && (
            <span className="hidden shrink-0 truncate font-ui text-step--2 font-medium uppercase tracking-[0.18em] text-ink-muted md:block">
              {item.categoryTitle}
            </span>
          )}
        </div>

        <h2 className="mt-3 font-ui text-step-1 leading-tight text-ink transition-colors duration-fast ease-sheet group-hover:text-apricot">
          {item.title}
          {item.externalUrl && (
            <ExternalLink
              size={14}
              aria-hidden="true"
              className="ml-1.5 inline align-[-2px] text-ink-muted"
            />
          )}
        </h2>

        {item.summary && (
          <p className="mt-3 line-clamp-2 text-step-0 text-ink-muted">{item.summary}</p>
        )}

        <span className="mt-5 inline-flex w-fit items-center gap-1.5 border-b border-rule-strong pb-0.5 font-ui text-step--1 text-ink transition-transform duration-fast ease-sheet group-hover:translate-x-1">
          Read the record
          <ArrowRight size={14} aria-hidden="true" />
        </span>
      </div>
    </NavLink>
  </article>
);

export default HubItemDispatch;
